import { useRef } from 'react';
import { useT } from '@/i18n/useT';
import { Divider } from '@/ui/components/Divider';
import { IconButton } from '@/ui/components/IconButton';
import { Tabs, type TabItem } from '@/ui/components/Tabs';
import { Icon } from '@/ui/icons/Icon';
import { WORKSPACE_MODES, useUiStore, type WorkspaceMode } from '@/store/uiStore';
import { MODE_TABS_PREFIX } from '@/ui/tabs/ids';
import { Toolbar, ToolbarGroup } from './Toolbar';
import { ToolbarButton } from './ToolbarButton';
import { useLadderEditorStore } from '@/ui/editor/ladder/store/ladderEditorStore';
import { useProjectStore } from '@/store/projectStore';
import { useSimulationStore } from '@/store/simulationStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useWorkshopStore } from '@/workshop/store/workshopStore';
import styles from './Toolbar.module.css';

export function AppToolbar() {
  const t = useT();
  const activeMode = useUiStore((state) => state.activeMode);
  const setMode = useUiStore((state) => state.setMode);
  const setSettingsOpen = useUiStore((state) => state.setSettingsOpen);
  const theme = useUiStore((state) => state.theme);
  const setTheme = useUiStore((state) => state.setTheme);
  const simState = useSimulationStore((state) => state.snapshot.state);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleNewProject = () => {
    if (useProjectStore.getState().isDirty) {
      if (!window.confirm(t('toolbar.confirmNew') || 'Kaydedilmemiş değişiklikler var. Yeni projeye başlamak istiyor musunuz?')) {
        return;
      }
    }
    useProjectStore.getState().newProject();
    useWorkshopStore.getState().resetCircuit();
  };

  const handleOpenProject = () => {
    if (useProjectStore.getState().isDirty) {
      if (!window.confirm(t('toolbar.confirmOpen') || 'Kaydedilmemiş değişiklikler var. Başka bir proje açmak istiyor musunuz?')) {
        return;
      }
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = event.target?.result as string;
        const projectData = JSON.parse(json);
        
        // Stop and reset any ongoing simulation
        useSimulationStore.getState().stop();
        useSimulationStore.getState().reset();

        // Clear UI selection and ladder editor history
        useUiStore.getState().clearSelection();
        useLadderEditorStore.getState().clearSelection();
        useLadderEditorStore.getState().clearHistory();

        // Load into project store (automatically normalized)
        useProjectStore.getState().loadProject(projectData);

        // Synchronize simulation engine with loaded program & devices
        const loadedProject = useProjectStore.getState().project;
        useSimulationStore.getState().engine.loadProgram(loadedProject.plc.program);
        useSimulationStore.getState().engine.process.loadDevices(
          loadedProject.scene.devices,
          loadedProject.scene.pipes || []
        );

        // Synchronize workshop circuit if present in loaded project
        if (loadedProject.workshop) {
          useWorkshopStore.getState().setProject(loadedProject.workshop);
        }

        useNotificationStore.getState().notify('toast.projectOpened', 'success');
      } catch (err) {
        useNotificationStore.getState().notify('toast.projectOpenError', 'error');
        console.error('Failed to parse project JSON', err);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const handleSaveProject = () => {
    try {
      // Sync current workshop circuit into project store before serializing
      const currentWorkshop = useWorkshopStore.getState().project;
      useProjectStore.getState().setWorkshopProject(currentWorkshop);

      const projectData = useProjectStore.getState().project;
      const json = JSON.stringify(projectData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectData.metadata.name || 'project'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      useProjectStore.getState().clearDirty();
      useNotificationStore.getState().notify('toast.projectSaved', 'success');
    } catch (err) {
      useNotificationStore.getState().notify('toast.projectSaveError', 'error');
      console.error('Failed to save project', err);
    }
  };

  const handleNotImplemented = () => {
    useNotificationStore.getState().notify('toast.undoRedoWip', 'info');
  };

  const modeTabs: readonly TabItem<WorkspaceMode>[] = WORKSPACE_MODES.map((mode) => ({
    id: mode,
    label: t(`mode.${mode}`),
    icon: `mode-${mode}`,
    description: t(`mode.${mode}.description`),
  }));

  return (
    <Toolbar label={t('toolbar.label')}>
      <div className={styles.brand}>
        <Icon name="app" size={26} />
        <span className={styles.brandName}>{t('app.name')}</span>
      </div>

      <ToolbarGroup label={t('toolbar.group.file')}>
        <ToolbarButton available icon="new" label={t('toolbar.new')} onClick={handleNewProject} />
        <ToolbarButton available icon="open" label={t('toolbar.open')} onClick={handleOpenProject} />
        <ToolbarButton available icon="save" label={t('toolbar.save')} onClick={handleSaveProject} />
        <input 
          type="file" 
          accept=".json" 
          style={{ display: 'none' }} 
          ref={fileInputRef} 
          onChange={handleFileChange} 
        />
      </ToolbarGroup>
      <Divider />
      <ToolbarGroup label={t('toolbar.group.edit')}>
        <ToolbarButton available icon="undo" label={t('toolbar.undo')} onClick={handleNotImplemented} />
        <ToolbarButton available icon="redo" label={t('toolbar.redo')} onClick={handleNotImplemented} />
      </ToolbarGroup>

      <div className={styles.spacer} />
      <Tabs
        variant="mode"
        label={t('toolbar.group.modes')}
        idPrefix={MODE_TABS_PREFIX}
        tabs={modeTabs}
        activeId={activeMode}
        onChange={setMode}
      />
      <div className={styles.spacer} />

      <ToolbarGroup label={t('toolbar.group.run')}>
        <ToolbarButton available={simState === 'STOPPED'} icon="save" label="Compile" onClick={() => {
           const success = useLadderEditorStore.getState().compile();
           if (success) {
             const prog = useProjectStore.getState().project.plc.program;
             const devices = useProjectStore.getState().project.scene.devices;
             const pipes = useProjectStore.getState().project.scene.pipes || [];
             useSimulationStore.getState().engine.loadProgram(prog);
             useSimulationStore.getState().engine.process.loadDevices(devices, pipes);
             useNotificationStore.getState().notify('toast.compileSuccess', 'success');
           } else {
             useUiStore.getState().setBottomTab('diagnostics');
             useNotificationStore.getState().notify('toast.compileFailed', 'error');
           }
        }} />
        <ToolbarButton 
          available={simState !== 'RUNNING'}
          icon="run" 
          label={t('toolbar.run')} 
          style={simState !== 'RUNNING' ? { color: 'var(--color-success)' } : undefined}
          onClick={() => {
            const success = useLadderEditorStore.getState().compile();
            if (success) {
              const prog = useProjectStore.getState().project.plc.program;
              const devices = useProjectStore.getState().project.scene.devices;
              const pipes = useProjectStore.getState().project.scene.pipes || [];
              useSimulationStore.getState().engine.loadProgram(prog);
              useSimulationStore.getState().engine.process.loadDevices(devices, pipes);
              useSimulationStore.getState().start();
              const currentMode = useUiStore.getState().activeMode;
              const hasHMI = useProjectStore.getState().project.hmi.screens.some((s) => s.components.length > 0);
              if (currentMode === 'design' || currentMode === 'hmi') {
                useUiStore.getState().setMode('simulation');
              }
              if (hasHMI || currentMode === 'hmi') {
                useUiStore.getState().setHmiOverlayOpen(true);
              }
            } else {
              useUiStore.getState().setBottomTab('diagnostics');
              useNotificationStore.getState().notify('toast.compileFailed', 'error');
            }
          }} 
        />
        <ToolbarButton 
          available={simState === 'RUNNING'}
          icon="pause" 
          label={t('toolbar.pause')} 
          style={simState === 'RUNNING' ? { color: 'var(--color-warning)' } : undefined}
          onClick={() => useSimulationStore.getState().pause()} 
        />
        <ToolbarButton 
          available={simState === 'PAUSED'}
          icon="step" 
          label={t('toolbar.step')} 
          style={simState === 'PAUSED' ? { color: 'var(--color-primary)' } : undefined}
          onClick={() => useSimulationStore.getState().step()} 
        />
        <ToolbarButton 
          available={simState !== 'STOPPED'}
          icon="stop" 
          label={t('toolbar.stop') || 'Stop'} 
          style={simState !== 'STOPPED' ? { color: 'var(--color-danger)' } : undefined}
          onClick={() => {
            useSimulationStore.getState().stop();
            if (useUiStore.getState().activeMode === 'simulation') {
              useUiStore.getState().setMode('design');
            }
          }} 
        />
        <ToolbarButton 
          available={true}
          icon="reset" 
          label={t('toolbar.reset') || 'Reset'} 
          style={{ color: 'var(--color-text-secondary)' }}
          onClick={() => {
            useSimulationStore.getState().reset();
            if (useUiStore.getState().activeMode === 'simulation') {
              useUiStore.getState().setMode('design');
            }
          }} 
        />
      </ToolbarGroup>
      <Divider />
      <ToolbarButton
        available={true}
        icon={theme === 'dark' ? 'theme-light' : 'theme-dark'}
        label={theme === 'dark' ? t('toolbar.theme.light') : t('toolbar.theme.dark')}
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        placement="bottom-end"
      />
      <IconButton
        icon="settings"
        label={t('toolbar.settings')}
        placement="bottom-end"
        onClick={() => setSettingsOpen(true)}
      />
      <ToolbarButton icon="help" label={t('toolbar.help')} placement="bottom-end" />
    </Toolbar>
  );
}
